import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import { AppUrlConstants } from '../../../core/constants/AppUrlConstants';
import * as XLSX from 'xlsx';

interface QueryRow {
  chatLogId?: number;
  id?: number;
  sessionId?: string;
  question?: string;
  userQuestion?: string;
  answer?: string;
  matchedAnswer?: string;
  abusive?: boolean;
  createdAt: string;
}

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

@Component({
  selector: 'app-queries',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './queries.component.html',
  styleUrl: './queries.component.css'
})
export class QueriesComponent implements OnInit {

  data: QueryRow[] = [];
  loading = true;
  search = '';
  page = 1;
  rowsPerPage = 10;
  toasts: Toast[] = [];
  showDeleteModal = false;
  showAddModal = false;
  showEditModal = false;
  deleteTargetRow: QueryRow | null = null;
  skeletonRows = Array.from({ length: 8 });
  // previewPage = 1;
  // previewRowsPerPage = 10;

  // Add modal fields
  newQuestion = '';
  newAnswer = '';
  newAbusive = false;

  // Edit modal fields
  editTargetRow: QueryRow | null = null;
  editQuestion = '';
  editAnswer = '';
  editAbusive = false;

  requiredColumns = ['question', 'answer', 'is_abusive'];
  showPreviewModal = false;
  previewData: any[] = [];

  private toastIdCounter = 0;

  constructor(private http: HttpClient) { }

  ngOnInit(): void {
    this.fetchData();
  }

  getQuestion(row: QueryRow): string {
    return row.question || row.userQuestion || '';
  }

  getAnswer(row: QueryRow): string {
    return row.answer || row.matchedAnswer || '';
  }

  getRowId(row: QueryRow): number {
    return (row.chatLogId ?? row.id)!;
  }

  // ─── Filtered rows ───
  get filtered(): QueryRow[] {
    return this.data.filter(row => {
      const q = this.getQuestion(row).toLowerCase();
      const a = this.getAnswer(row).toLowerCase();
      return !this.search ||
        q.includes(this.search.toLowerCase()) ||
        a.includes(this.search.toLowerCase());
    });
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filtered.length / this.rowsPerPage));
  }

  get pageData(): QueryRow[] {
    return this.filtered.slice((this.page - 1) * this.rowsPerPage, this.page * this.rowsPerPage);
  }

  // ─── Fetch ───
  fetchData(): void {
    this.loading = true;
    this.http.get<any>(AppUrlConstants.GET_QUERIES).subscribe({
      next: (json) => {
        const list = Array.isArray(json) ? json : json.data || json.results || [];
        this.data = list.sort((a: QueryRow, b: QueryRow) => this.getRowId(a) - this.getRowId(b));
        this.loading = false;
      },
      error: () => {
        this.addToast('Failed to load queries', 'error');
        this.data = [];
        this.loading = false;
      }
    });
  }

  // ─── Async training task submission ───
  // All Add/Update/Delete/Bulk-import actions create a background task via ONE
  // API; nothing is saved immediately. Progress is shown on Training History,
  // so the queries list is NOT refreshed here.
  private submitTrainingTask(
    processType: 'ADD' | 'UPDATE' | 'DELETE',
    payloads: { question: string; answer: string; isAbusive: boolean; operationType: string }[],
    onDone: () => void
  ): void {
    const body = { processType, totalRows: payloads.length, payloads };
    this.http.post<any>(AppUrlConstants.ADD_TRAINING_DATA, body).subscribe({
      next: () => {
        onDone();
        this.addToast('Request submitted successfully. Track progress in Training History.');
      },
      error: (err) => {
        const msg = err?.error?.message || 'Failed to submit request';
        this.addToast(msg, 'error');
      }
    });
  }

  // ─── Add ───
  openAddModal(): void {
    this.newQuestion = '';
    this.newAnswer = '';
    this.newAbusive = false;
    this.showAddModal = true;
  }

  closeAddModal(): void {
    this.showAddModal = false;
    this.newAbusive = false;
  }

  handleAdd(): void {
    const q = this.newQuestion.trim();
    const a = this.newAnswer.trim();
    if (!q || !a) {
      this.addToast('Please fill in both question and answer', 'error');
      return;
    }

    this.submitTrainingTask(
      'ADD',
      [{ question: q, answer: a, isAbusive: this.newAbusive, operationType: 'ADD' }],
      () => this.closeAddModal()
    );
  }

  // ─── Edit ───
  openEditModal(row: QueryRow): void {
    this.editTargetRow = row;
    this.editQuestion = this.getQuestion(row);
    this.editAnswer = this.getAnswer(row);
    this.editAbusive = row.abusive ?? false;
    this.showEditModal = true;
  }

  closeEditModal(): void {
    this.showEditModal = false;
    this.editTargetRow = null;
  }

  handleEdit(): void {
    if (!this.editTargetRow) return;
    const q = this.editQuestion.trim();
    const a = this.editAnswer.trim();
    if (!q || !a) {
      this.addToast('Please fill in both question and answer', 'error');
      return;
    }

    // The worker matches rows by question text — editing the question text
    // itself creates a new query row rather than renaming the existing one.
    this.submitTrainingTask(
      'UPDATE',
      [{ question: q, answer: a, isAbusive: this.editAbusive, operationType: 'UPDATE' }],
      () => this.closeEditModal()
    );
  }

  // ─── Delete ───
  confirmDeleteRow(row: QueryRow): void {
    this.deleteTargetRow = row;
    this.showDeleteModal = true;
  }

  handleDelete(): void {
    this.showDeleteModal = false;
    if (!this.deleteTargetRow) return;

    const row = this.deleteTargetRow;
    this.deleteTargetRow = null;

    // The worker locates the row to delete by question text
    this.submitTrainingTask(
      'DELETE',
      [{
        question: this.getQuestion(row),
        answer: this.getAnswer(row),
        isAbusive: row.abusive ?? false,
        operationType: 'DELETE'
      }],
      () => { }
    );
  }

  // ─── Toast ───
  addToast(message: string, type: 'success' | 'error' = 'success'): void {
    const id = ++this.toastIdCounter;
    this.toasts.push({ id, message, type });
    setTimeout(() => this.removeToast(id), 3000);
  }

  removeToast(id: number): void {
    this.toasts = this.toasts.filter(t => t.id !== id);
  }

  // ─── Filters ───
  onSearchChange(): void { this.page = 1; }

  clearFilters(): void {
    this.search = '';
    this.page = 1;
  }

  // ─── Helpers ───
  getRowClass(idx: number): string {
    return idx % 2 === 0 ? 'row-even' : 'row-odd';
  }

  getRowIndex(idx: number): number {
    return (this.page - 1) * this.rowsPerPage + idx + 1;
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) return;

    event.target.value = '';

    // Validate file type
    const allowedExtensions = ['xlsx', 'xls'];
    const extension = file.name.split('.').pop()?.toLowerCase();

    if (!allowedExtensions.includes(extension || '')) {
      this.addToast('Only Excel files are allowed', 'error');
      return;
    }

    const reader = new FileReader();

    reader.onload = (e: any) => {
      const binaryString = e.target.result;
      const workbook = XLSX.read(binaryString, { type: 'binary' });

      // Validate single sheet
      if (workbook.SheetNames.length !== 1) {
        this.addToast('Only single sheet is allowed', 'error');
        return;
      }

      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      const rawData: any[] = XLSX.utils.sheet_to_json(worksheet, {
        defval: ''
      });

      if (!rawData.length) {
        this.addToast('Excel file is empty', 'error');
        return;
      }

      // Validate columns
      const headers = Object.keys(rawData[0]);

      const isValidColumns =
        headers.length === this.requiredColumns.length &&
        this.requiredColumns.every(col => headers.includes(col));

      if (!isValidColumns) {
        this.addToast(
          'Columns must be exactly: question, answer, is_abusive',
          'error'
        );
        return;
      }

      // Clean data
      let cleanedData = rawData
        .map(row => ({
          question: String(row.question || '').trim(),
          answer: String(row.answer || '').trim(),
          is_abusive: this.convertToBoolean(row.is_abusive)
        }))
        .filter(row => row.question && row.answer);

      // Validate boolean
      const invalidBoolean = cleanedData.some(
        row => typeof row.is_abusive !== 'boolean'
      );

      if (invalidBoolean) {
        this.addToast('is_abusive must be boolean', 'error');
        return;
      }
      this.previewData = cleanedData;
      this.showPreviewModal = true;
    };

    reader.readAsBinaryString(file);
  }

  convertToBoolean(value: any): boolean | null {
    if (
      value === true ||
      value === 'true' ||
      value === 'TRUE' ||
      value === 1 ||
      value === '1'
    ) return true;

    if (
      value === false ||
      value === 'false' ||
      value === 'FALSE' ||
      value === 0 ||
      value === '0'
    ) return false;

    return null;
  }
  confirmUpload(): void {
    if (!this.previewData.length) return;

    // Bulk import uses the SAME task API — one ADD task with all rows
    const payloads = this.previewData.map(r => ({
      question: r.question,
      answer: r.answer,
      isAbusive: r.is_abusive === true,
      operationType: 'ADD'
    }));

    this.submitTrainingTask('ADD', payloads, () => this.cancelPreview());
  }

  cancelPreview(): void {
    this.showPreviewModal = false;
    this.previewData = [];
  }
}
